import { gql, useMutation } from '@apollo/client';
import { type CalendarDate } from '@internationalized/date';
import { DatePicker } from '@orn/shared-web/components/DatePicker';
import Form from '@orn/shared-web/components/Form';
import Modal from '@orn/shared-web/components/Modal';
import Select from '@orn/shared-web/components/Select';
import TextArea from '@orn/shared-web/components/Textarea';
import dollarsToCents from '@orn/shared-web/utils/money/dollarsToCents';
import * as React from 'react';

import { BillSource } from '../../__generated__/globalTypes';
import AdminSelectMerchant from '../../components/admin/AdminSelectMerchant';
import AdminSelectRecurringTransaction from '../../components/admin/AdminSelectRecurringTransaction';
import AdminSelectUser, { type IAdminSelectUserSelection } from '../../components/admin/AdminSelectUser';
import Button from '../../components/Button';
import Input from '../../components/Input';
import { useToast } from '../../components/Toast';

import { autoPayOptions, getDatePickerValue } from './billFormHelpers';

import type {
  AdminCreateBillMutation as AdminCreateBillMutationType,
  AdminCreateBillMutationVariables,
} from './__generated__/CreateBillModal.gql';

const ADMIN_CREATE_BILL = gql`
  mutation AdminCreateBill($input: AdminCreateBillInput!) {
    adminCreateBill(input: $input) {
      id
    }
  }
`;

interface ICreateBillModalProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
}

const billSourceOptions: Array<{ id: BillSource; label: string }> = [
  { id: BillSource.User, label: 'User' },
  { id: BillSource.Email, label: 'Email' },
  { id: BillSource.Sms, label: 'SMS' },
];

/**
 * Modal that creates a `Bill` for the selected user via the mops-api
 * `adminCreateBill` mutation. Echoes the original `@orn/web` admin form —
 * same field surface, same provider/merchant auto-fill behavior — re-skinned
 * for the MOPS aesthetic and wired through MOPS-local `Input`/`Button`.
 */
const CreateBillModal = ({ isOpen, onOpenChange }: ICreateBillModalProps) => {
  const [selection, setSelection] = React.useState<IAdminSelectUserSelection | null>(null);
  const [merchantId, setMerchantId] = React.useState('');
  const [recurringTransactionId, setRecurringTransactionId] = React.useState('');
  const [billSubmissionId, setBillSubmissionId] = React.useState('');
  const [source, setSource] = React.useState<BillSource>(BillSource.User);
  const [providerName, setProviderName] = React.useState('');
  const [amountDue, setAmountDue] = React.useState('');
  const [dueDate, setDueDate] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [accountNumber, setAccountNumber] = React.useState('');
  const [invoiceNumber, setInvoiceNumber] = React.useState('');
  const [minAmountDue, setMinAmountDue] = React.useState('');
  const [statementDate, setStatementDate] = React.useState('');
  const [serviceStartDate, setServiceStartDate] = React.useState('');
  const [serviceEndDate, setServiceEndDate] = React.useState('');
  const [isAutopay, setIsAutopay] = React.useState<'unset' | 'true' | 'false'>('unset');
  const [paymentUrl, setPaymentUrl] = React.useState('');
  const [lateFeeAmount, setLateFeeAmount] = React.useState('');
  const [lateFeeDate, setLateFeeDate] = React.useState('');
  const [providerPhone, setProviderPhone] = React.useState('');
  const [providerEmail, setProviderEmail] = React.useState('');
  const [payerName, setPayerName] = React.useState('');
  const [previousAccountBalance, setPreviousAccountBalance] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const { showToast } = useToast();

  const [createBill, { loading }] = useMutation<AdminCreateBillMutationType, AdminCreateBillMutationVariables>(
    ADMIN_CREATE_BILL,
    {
      refetchQueries: selection ? ['AdminListBills'] : [],
      awaitRefetchQueries: true,
    }
  );

  const resetForm = React.useCallback(() => {
    setSelection(null);
    setMerchantId('');
    setRecurringTransactionId('');
    setBillSubmissionId('');
    setSource(BillSource.User);
    setProviderName('');
    setAmountDue('');
    setDueDate('');
    setDescription('');
    setAccountNumber('');
    setInvoiceNumber('');
    setMinAmountDue('');
    setStatementDate('');
    setServiceStartDate('');
    setServiceEndDate('');
    setIsAutopay('unset');
    setPaymentUrl('');
    setLateFeeAmount('');
    setLateFeeDate('');
    setProviderPhone('');
    setProviderEmail('');
    setPayerName('');
    setPreviousAccountBalance('');
    setError(null);
  }, []);

  React.useEffect(() => {
    if (!isOpen) {
      resetForm();
    }
  }, [isOpen, resetForm]);

  const handleClose = () => {
    onOpenChange(false);
    resetForm();
  };

  const isAmountDueValid = amountDue.trim().length > 0 && !Number.isNaN(Number(amountDue));
  const isCreateBillDisabled =
    loading || !selection || providerName.trim().length === 0 || dueDate.trim().length === 0 || !isAmountDueValid;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!selection) {
      setError('Please select a user.');
      return;
    }

    if (providerName.trim().length === 0) {
      setError('Provider name is required.');
      return;
    }

    if (dueDate.trim().length === 0) {
      setError('Due date is required.');
      return;
    }

    const parsedAmountDue = Number(amountDue);
    if (amountDue.trim().length === 0 || Number.isNaN(parsedAmountDue)) {
      setError('Amount due must be a valid number.');
      return;
    }
    const amountDueInCents = dollarsToCents(parsedAmountDue);

    const parseOptionalAmountInCents = (value: string, label: string): number | null | undefined => {
      if (value.trim().length === 0) {
        return undefined;
      }
      const parsedValue = Number(value);
      if (Number.isNaN(parsedValue)) {
        setError(`${label} must be a valid number.`);
        return null;
      }
      return dollarsToCents(parsedValue);
    };

    const parsedMinAmountDue = parseOptionalAmountInCents(minAmountDue, 'Min amount due');
    if (parsedMinAmountDue === null) {
      return;
    }
    const parsedLateFeeAmount = parseOptionalAmountInCents(lateFeeAmount, 'Late fee amount');
    if (parsedLateFeeAmount === null) {
      return;
    }
    const parsedPreviousAccountBalance = parseOptionalAmountInCents(previousAccountBalance, 'Previous account balance');
    if (parsedPreviousAccountBalance === null) {
      return;
    }

    // CalendarDate scalar serializes as `YYYY-MM-DD` on the wire; codegen types it as
    // CalendarDate (object) for both input and output. Cast through `unknown` to send a string.
    const asCalendarDate = (value: string) => value as unknown as CalendarDate;

    try {
      await createBill({
        variables: {
          input: {
            familyId: selection.familyId,
            userId: selection.userId,
            source,
            providerName: providerName.trim(),
            amountDue: amountDueInCents,
            dueDate: asCalendarDate(dueDate.trim()),
            ...(billSubmissionId.trim().length > 0 ? { billSubmissionId: billSubmissionId.trim() } : {}),
            ...(merchantId.trim().length > 0 ? { merchantId: merchantId.trim() } : {}),
            ...(recurringTransactionId.trim().length > 0
              ? { recurringTransactionId: recurringTransactionId.trim() }
              : {}),
            ...(description.trim().length > 0 ? { description: description.trim() } : {}),
            ...(accountNumber.trim().length > 0 ? { accountNumber: accountNumber.trim() } : {}),
            ...(invoiceNumber.trim().length > 0 ? { invoiceNumber: invoiceNumber.trim() } : {}),
            ...(parsedMinAmountDue != null ? { minAmountDue: parsedMinAmountDue } : {}),
            ...(statementDate.trim().length > 0 ? { statementDate: asCalendarDate(statementDate.trim()) } : {}),
            ...(serviceStartDate.trim().length > 0
              ? { serviceStartDate: asCalendarDate(serviceStartDate.trim()) }
              : {}),
            ...(serviceEndDate.trim().length > 0 ? { serviceEndDate: asCalendarDate(serviceEndDate.trim()) } : {}),
            ...(isAutopay !== 'unset' ? { isAutopay: isAutopay === 'true' } : {}),
            ...(paymentUrl.trim().length > 0 ? { paymentUrl: paymentUrl.trim() } : {}),
            ...(parsedLateFeeAmount != null ? { lateFeeAmount: parsedLateFeeAmount } : {}),
            ...(lateFeeDate.trim().length > 0 ? { lateFeeDate: asCalendarDate(lateFeeDate.trim()) } : {}),
            ...(providerPhone.trim().length > 0 ? { providerPhone: providerPhone.trim() } : {}),
            ...(providerEmail.trim().length > 0 ? { providerEmail: providerEmail.trim() } : {}),
            ...(payerName.trim().length > 0 ? { payerName: payerName.trim() } : {}),
            ...(parsedPreviousAccountBalance != null ? { previousAccountBalance: parsedPreviousAccountBalance } : {}),
          },
        },
      });

      showToast({ type: 'success', message: 'Bill created.' });
      resetForm();
      onOpenChange(false);
    } catch {
      // ApolloProvider's errorLink reports GraphQL + network errors to Sentry.
      showToast({ type: 'error', message: 'Failed to create bill. Please try again.' });
    }
  };

  return (
    <Modal isOpen={isOpen} onOpenChange={onOpenChange} size="lg" className="h-[80vh] max-h-[80vh]">
      <Modal.Header onClose={handleClose}>Create bill</Modal.Header>
      <Modal.Body className="min-h-0 flex-1 items-start px-0 pb-6 pt-0">
        <Form onSubmit={handleSubmit} className="flex h-full min-h-0 w-full flex-col">
          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
            <div className="flex flex-col gap-4 px-6">
              <AdminSelectUser
                selectedUserId={selection?.userId ?? null}
                onSelectionChange={setSelection}
                className="w-full"
              />

              <AdminSelectMerchant
                selectedFamilyId={selection?.familyId ?? null}
                selectedMerchantId={merchantId || null}
                onSelectionChange={(value, merchantName) => {
                  setMerchantId(value ?? '');
                  if (merchantName && merchantName.trim().length > 0) {
                    setProviderName(merchantName);
                  }
                }}
                className="w-full"
              />
              <AdminSelectRecurringTransaction
                selectedFamilyId={selection?.familyId ?? null}
                selectedRecurringTransactionId={recurringTransactionId || null}
                onSelectionChange={(value) => setRecurringTransactionId(value ?? '')}
                className="w-full"
              />

              <Input
                label="Bill submission ID (optional)"
                value={billSubmissionId}
                onChange={setBillSubmissionId}
                placeholder="bill_submission_123"
              />

              <Select
                label="Source"
                selectedKey={source}
                onSelectionChange={(key) => setSource(key as BillSource)}
                items={billSourceOptions}
              >
                {(item) => <Select.Item id={item.id}>{item.label}</Select.Item>}
              </Select>

              <Input
                label="Provider name"
                value={providerName}
                onChange={setProviderName}
                placeholder="Provider name"
                required
              />

              <Input label="Amount due" value={amountDue} onChange={setAmountDue} placeholder="120.34" required />

              <div className="flex w-full flex-col gap-1.5">
                <div className="text-fg-secondary flex items-center gap-0.5 text-sm font-medium">
                  Due date <span className="text-fg-brand-primary">*</span>
                </div>
                <DatePicker
                  value={getDatePickerValue(dueDate)}
                  onChange={(dateValue) => setDueDate(dateValue ? dateValue.toString() : '')}
                />
              </div>

              <TextArea
                label="Description (optional)"
                value={description}
                onChange={setDescription}
                rows={3}
                placeholder="Bill description"
              />

              <Input
                label="Account number (optional)"
                value={accountNumber}
                onChange={setAccountNumber}
                placeholder="1234567890"
              />
              <Input
                label="Invoice number (optional)"
                value={invoiceNumber}
                onChange={setInvoiceNumber}
                placeholder="INV-1001"
              />
              <Input
                label="Min amount due (optional)"
                value={minAmountDue}
                onChange={setMinAmountDue}
                placeholder="45.67"
              />
              <div className="flex w-full flex-col gap-1.5">
                <div className="text-fg-secondary text-sm font-medium">Statement date (optional)</div>
                <DatePicker
                  value={getDatePickerValue(statementDate)}
                  onChange={(dateValue) => setStatementDate(dateValue ? dateValue.toString() : '')}
                />
              </div>
              <div className="flex w-full flex-col gap-1.5">
                <div className="text-fg-secondary text-sm font-medium">Service start date (optional)</div>
                <DatePicker
                  value={getDatePickerValue(serviceStartDate)}
                  onChange={(dateValue) => setServiceStartDate(dateValue ? dateValue.toString() : '')}
                />
              </div>
              <div className="flex w-full flex-col gap-1.5">
                <div className="text-fg-secondary text-sm font-medium">Service end date (optional)</div>
                <DatePicker
                  value={getDatePickerValue(serviceEndDate)}
                  onChange={(dateValue) => setServiceEndDate(dateValue ? dateValue.toString() : '')}
                />
              </div>
              <Select
                label="Is autopay (optional)"
                selectedKey={isAutopay}
                onSelectionChange={(key) => setIsAutopay(key as 'unset' | 'true' | 'false')}
                items={autoPayOptions}
              >
                {(item) => <Select.Item id={item.id}>{item.label}</Select.Item>}
              </Select>
              <Input
                label="Payment URL (optional)"
                value={paymentUrl}
                onChange={setPaymentUrl}
                placeholder="https://payments.example.com"
              />
              <Input
                label="Late fee amount (optional)"
                value={lateFeeAmount}
                onChange={setLateFeeAmount}
                placeholder="12.00"
              />
              <div className="flex w-full flex-col gap-1.5">
                <div className="text-fg-secondary text-sm font-medium">Late fee date (optional)</div>
                <DatePicker
                  value={getDatePickerValue(lateFeeDate)}
                  onChange={(dateValue) => setLateFeeDate(dateValue ? dateValue.toString() : '')}
                />
              </div>
              <Input
                label="Provider phone (optional)"
                value={providerPhone}
                onChange={setProviderPhone}
                placeholder="+1 555 555 5555"
              />
              <Input
                label="Provider email (optional)"
                value={providerEmail}
                onChange={setProviderEmail}
                placeholder="billing@provider.com"
              />
              <Input label="Payer name (optional)" value={payerName} onChange={setPayerName} placeholder="Jane Doe" />
              <Input
                label="Previous account balance (optional)"
                value={previousAccountBalance}
                onChange={setPreviousAccountBalance}
                placeholder="300.00"
              />
            </div>
          </div>

          <div className="border-border-primary mt-3 border-t px-6 pt-3">
            {error && <div className="text-fg-error-primary mb-3 text-sm">{error}</div>}
            <div className="flex w-full gap-3">
              <Button color="secondary" onClick={handleClose} className="flex-1" isDisabled={loading}>
                Cancel
              </Button>
              <Button
                type="submit"
                color="primary"
                className="flex-1"
                isDisabled={isCreateBillDisabled}
                isLoading={loading}
              >
                Create bill
              </Button>
            </div>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default CreateBillModal;
